import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaLeaf, FaBell, FaGlobe, FaChevronDown, 
  FaUserCircle, FaBars, FaTimes, FaMapMarkerAlt, FaCloudSun,
  FaSun, FaMoon 
} from 'react-icons/fa';
import './Navbar.css';

const Navbar = ({ farmerProfile, weatherTelemetry }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [locationPopoverOpen, setLocationPopoverOpen] = useState(false);
  const [locSearchInput, setLocSearchInput] = useState('');
  const [locSearchResults, setLocSearchResults] = useState([]);
  const [detectingGps, setDetectingGps] = useState(false);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('fd_theme') === 'dark' || document.documentElement.getAttribute('data-theme') === 'dark';
  });

  const toggleDarkMode = () => {
    const nextMode = !isDarkMode;
    setIsDarkMode(nextMode);
    if (nextMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.classList.add('dark-mode');
      const rootEl = document.getElementById('root');
      if (rootEl) rootEl.classList.add('dark-mode');
      localStorage.setItem('fd_theme', 'dark');
      window.dispatchEvent(new CustomEvent('theme-change', { detail: { theme: 'dark' } }));
    } else {
      document.documentElement.removeAttribute('data-theme');
      document.body.classList.remove('dark-mode');
      const rootEl = document.getElementById('root');
      if (rootEl) rootEl.classList.remove('dark-mode');
      localStorage.setItem('fd_theme', 'light');
      window.dispatchEvent(new CustomEvent('theme-change', { detail: { theme: 'light' } }));
    }
  };

  const isInsideApp = [
    '/dashboard', '/check-risk', '/risk-result', 
    '/interventions', '/advisory', '/schemes', '/support',
    '/market', '/weather', '/history', '/profile', '/settings'
  ].includes(location.pathname);

  const currentLang = i18n.language || 'mr';

  const languages = [
    { code: 'hi', label: 'हिंदी (Hindi)' },
    { code: 'en', label: 'English' },
    { code: 'mr', label: 'मराठी (Marathi)' },
    { code: 'gu', label: 'ગુજરાતી (Gujarati)' }
  ];

  const quickLocations = [
    { name_mr: 'जळगाव (केळी)', name_hi: 'जलगांव (केला)', name_en: 'Jalgaon (Banana)', id: 'jalgaon', lat: 21.0077, lon: 75.5626 },
    { name_mr: 'नाशिक (द्राक्षे/कांदा)', name_hi: 'नासिक (अंगूर/प्याज)', name_en: 'Nashik (Grapes/Onion)', id: 'nashik', lat: 19.9975, lon: 73.7898 },
    { name_mr: 'नागपूर (संत्री)', name_hi: 'नागपुर (संतरा)', name_en: 'Nagpur (Orange)', id: 'nagpur', lat: 21.1458, lon: 79.0882 },
    { name_mr: 'पुणे (ऊस/डाळिंब)', name_hi: 'पुणे (गन्ना)', name_en: 'Pune (Sugarcane)', id: 'pune', lat: 18.5204, lon: 73.8567 },
    { name_mr: 'इंदूर (सोयाबीन)', name_hi: 'इंदौर (सोयाबीन)', name_en: 'Indore (Soybean)', id: 'indore', lat: 22.7196, lon: 75.8577 }
  ];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setLangDropdownOpen(false);
  };

  const handleLocationSelect = (locName) => {
    localStorage.setItem('farmer_location', locName);
    window.dispatchEvent(new CustomEvent('farmer_location_changed', { detail: locName }));
    setLocationPopoverOpen(false);
    setLocSearchResults([]);
    setLocSearchInput('');
  };

  const handleLiveGpsDetect = () => {
    if (navigator.geolocation) {
      setDetectingGps(true);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const lat = pos.coords.latitude;
            const lon = pos.coords.longitude;
            const res = await fetch(`http://localhost:8000/api/auto-field-telemetry?lat=${lat}&lon=${lon}`);
            const d = await res.json();
            handleLocationSelect(d.location.formatted_address);
          } catch (e) {
            handleLocationSelect('Jalgaon, Maharashtra');
          } finally {
            setDetectingGps(false);
          }
        },
        () => {
          handleLocationSelect('Jalgaon, Maharashtra');
          setDetectingGps(false);
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }
  };

  const handleLocSearchChange = async (val) => {
    setLocSearchInput(val);
    if (!val || val.trim().length < 2) {
      setLocSearchResults([]);
      return;
    }
    try {
      const res = await fetch(`http://localhost:8000/api/search-village-location?q=${encodeURIComponent(val)}`);
      const data = await res.json();
      setLocSearchResults(data || []);
    } catch (e) {
      setLocSearchResults([]);
    }
  };

  const activeDisplayLocation = localStorage.getItem('farmer_location') || farmerProfile?.location || 'Jalgaon, Maharashtra';

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        if (sectionId === 'top') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    } else {
      if (sectionId === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className={`fd-navbar-container ${isInsideApp ? 'fd-nav-app' : 'fd-nav-landing'}`}>
      <div className="fd-navbar-inner">
        {/* Left: Brand Logo */}
        <Link to="/" className="fd-brand-logo" onClick={() => scrollToSection('top')}>
          <div className="fd-logo-icon-box">
            <FaLeaf className="fd-logo-leaf" />
          </div>
          <div className="fd-brand-text">
            <span className="fd-brand-title">Fasal Drishti</span>
            <span className="fd-brand-subtitle">Detect • Explain • Act</span>
          </div>
        </Link>

        {/* Center / Navigation Links (Landing Page Mode Only) */}
        {!isInsideApp && (
          <nav className={`fd-nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            <button className={`fd-nav-anchor-btn ${location.pathname === '/' ? 'active' : ''}`} onClick={() => scrollToSection('top')}>
              {currentLang === 'mr' ? 'मुख्यपृष्ठ' : (currentLang === 'hi' ? 'होम' : 'Home')}
            </button>
            <button className="fd-nav-anchor-btn" onClick={() => scrollToSection('about')}>
              {currentLang === 'mr' ? 'आमच्याबद्दल' : (currentLang === 'hi' ? 'हमारे बारे में' : 'About Us')}
            </button>
            <button className="fd-nav-anchor-btn" onClick={() => scrollToSection('how-it-works')}>
              {currentLang === 'mr' ? 'कसे कार्य करते' : (currentLang === 'hi' ? 'कैसे काम करता है' : 'How It Works')}
            </button>
            <button className="fd-nav-anchor-btn" onClick={() => scrollToSection('features')}>
              {currentLang === 'mr' ? 'वैशिष्ट्ये' : (currentLang === 'hi' ? 'सुविधाएं' : 'Features')}
            </button>
            <button className="fd-nav-anchor-btn" onClick={() => scrollToSection('contact')}>
              {currentLang === 'mr' ? 'संपर्क' : (currentLang === 'hi' ? 'संपर्क' : 'Contact')}
            </button>
          </nav>
        )}

        {/* Inside App Top Context: Location & Weather & Alerts */}
        {isInsideApp && (
          <div className="fd-nav-app-context hide-mobile">
            <div className="fd-location-selector-wrapper">
              <button 
                className="fd-context-pill fd-location-clickable" 
                onClick={() => setLocationPopoverOpen(!locationPopoverOpen)}
                title="Click to Change Farm Location"
              >
                <FaMapMarkerAlt className="fd-pill-icon red" />
                <span className="fd-pill-loc-text">{activeDisplayLocation}</span>
                <FaChevronDown className="fd-pill-chevron" />
              </button>

              {locationPopoverOpen && (
                <div className="fd-location-popover fd-card">
                  <div className="fd-popover-header">
                    <h4>{currentLang === 'mr' ? '📍 शेताचे ठिकाण बदला' : (currentLang === 'hi' ? '📍 खेत का स्थान बदलें' : '📍 Change Farm Location')}</h4>
                    <button className="fd-close-popover-btn" onClick={() => setLocationPopoverOpen(false)}>✕</button>
                  </div>

                  {/* 1-Tap GPS Auto-Detect */}
                  <button 
                    className="fd-popover-gps-btn" 
                    onClick={handleLiveGpsDetect}
                    disabled={detectingGps}
                  >
                    📡 {detectingGps ? (currentLang === 'mr' ? 'स्थान शोधत आहे...' : 'स्थान खोज रहा है...') : (currentLang === 'mr' ? 'GPS ने थेट स्थान शोधा' : (currentLang === 'hi' ? 'GPS से लाइव स्थान खोजें' : 'Auto-Detect Live GPS'))}
                  </button>

                  {/* Village / City Search Input */}
                  <div className="fd-popover-search">
                    <input 
                      type="text" 
                      placeholder={currentLang === 'mr' ? 'गाव / तालुका / शहर शोधा...' : (currentLang === 'hi' ? 'गांव / तालुका / शहर खोजें...' : 'Search Village / City...')} 
                      value={locSearchInput}
                      onChange={(e) => handleLocSearchChange(e.target.value)}
                    />
                    {locSearchResults.length > 0 && (
                      <div className="fd-popover-search-results">
                        {locSearchResults.map((r, i) => (
                          <div 
                            key={i} 
                            className="fd-search-result-row"
                            onClick={() => handleLocationSelect(r.display_name)}
                          >
                            📍 {r.display_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Quick Select Agricultural Hubs */}
                  <div className="fd-popover-quick-hubs">
                    <span className="fd-quick-label">{currentLang === 'mr' ? 'प्रमुख कृषी पट्टे:' : 'प्रमुख कृषि क्षेत्र:'}</span>
                    <div className="fd-quick-chips">
                      {quickLocations.map((q) => (
                        <button 
                          key={q.id}
                          className="fd-quick-chip"
                          onClick={() => handleLocationSelect(`${q.name_en.split(' ')[0]}, Maharashtra`)}
                        >
                          {currentLang === 'mr' ? q.name_mr : (currentLang === 'hi' ? q.name_hi : q.name_en)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="fd-context-pill">
              <FaCloudSun className="fd-pill-icon gold" />
              <span>{weatherTelemetry?.temp || '30.5'}°C {weatherTelemetry?.condition || 'Partly Cloudy'}</span>
            </div>
          </div>
        )}

        {/* Right Section: Language Dropdown + User Action */}
        <div className="fd-navbar-right">
          {/* Language Selector Dropdown */}
          <div className="fd-lang-selector-wrapper">
            <button 
              className="fd-lang-btn" 
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              aria-label="Select Language"
            >
              <FaGlobe className="fd-globe-icon" />
              <span className="fd-lang-label">
                {languages.find(l => l.code === currentLang)?.label.split(' ')[0] || (currentLang === 'mr' ? 'मराठी' : 'हिंदी')}
              </span>
              <FaChevronDown className="fd-chevron" />
            </button>

            {langDropdownOpen && (
              <div className="fd-lang-dropdown">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    className={`fd-lang-item ${currentLang === lang.code ? 'active' : ''}`}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dark / Light Theme Switcher */}
          <button 
            className="fd-theme-toggle-btn" 
            onClick={toggleDarkMode}
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            aria-label="Toggle Dark Mode"
          >
            {isDarkMode ? <FaSun className="fd-theme-icon sun" /> : <FaMoon className="fd-theme-icon moon" />}
          </button>

          {/* If inside app: Notifications & Profile chip */}
          {isInsideApp ? (
            <div className="fd-user-app-actions">
              <button className="fd-icon-btn-bell" title="Alerts" onClick={() => navigate('/dashboard')}>
                <FaBell />
                <span className="fd-bell-badge">2</span>
              </button>
              <Link to="/profile" className="fd-farmer-chip">
                <div className="fd-farmer-avatar">
                  <FaUserCircle />
                </div>
                <div className="fd-farmer-info hide-mobile">
                  <span className="fd-farmer-name">{farmerProfile?.name || 'Sanket Bhende'}</span>
                  <span className="fd-farmer-tag">{currentLang === 'mr' ? 'शेतकरी' : (currentLang === 'hi' ? 'किसान' : 'Farmer')}</span>
                </div>
              </Link>
            </div>
          ) : (
            /* If on Landing: Login / Get Started button */
            <Link to="/login" className="fd-btn-login">
              {currentLang === 'mr' ? 'लॉगिन / सुरू करा' : (currentLang === 'hi' ? 'लॉगिन / शुरू करें' : 'Login / Get Started')}
            </Link>
          )}

          {/* Mobile hamburger toggle */}
          {!isInsideApp && (
            <button 
              className="fd-mobile-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Menu"
            >
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
